import type { LobbyManagerStateResult } from "@/utils/api/useLobbyManagerState.js";
import type { ReactNode } from "react";
import React, { startTransition, useCallback, useEffect, useMemo, useState } from "react";
import type { LobbySummary } from "@rosette/ts/src/business/lobby/LobbySummary.js";
import { motion, AnimatePresence } from "framer-motion";
import { fadeInOut } from "@/utils/fadeInOut.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import { Button } from "@rosette/web_components/src/ui/Button.js";
import { LobbyPlayerDisplayName } from "@rosette/web_components/src/user/LobbyPlayerDisplayName.js";
import { Path } from "@rosette/ts/src/util/Path.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import Link from "next/link";
import { LoginPrompt } from "@/components/prompt/LoginPrompt.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import { useMatchmaking } from "@rosette/web_utils/src/game/useMatchmaking.js";
import { useGameAPI } from "@/components/api/GameAPIContext.js";
import { RemoteLobbySettings } from "@rosette/ts/src/business/game/RemoteLobbySettings.js";
import { RulesetPopupButton } from "@/components/gamesetup/RulesetPopupButton.js";
import type { ClientError } from "@rosette/ts/src/business/game/error/ClientError.js";
import { RulesetCounts } from "@rosette/ts/src/business/lobby/RulesetCounts.js";
import { DotDotDot } from "@rosette/web_components/src/game/timing/DotDotDot.js";
import CloseIcon from "@rosette/web_components/src/icon/CloseIcon.js";
import { FadingDiv } from "@/components/layout/FadingDiv.js";
import type { QueueState } from "@rosette/ts/src/business/game/queue/QueueState.js";
import type { QueueType } from "@rosette/ts/src/business/game/queue/QueueType.js";
import { areQueueTypesEqual } from "@rosette/ts/src/business/game/queue/QueueType.js";
import type { WaitingPlayerState } from "@rosette/ts/src/business/api/GameAPI.js";
import { Panel } from "@rosette/web_components/src/ui/Panel.js";
import { InlineIcon } from "@rosette/web_components/src/ui/InlineIcon.js";


export interface WaitingPlayerCounts {
    ranked: number;
    casual: number;
}


export class MatchmakingState {
    public readonly activeQueue: QueueState | null;
    public readonly requestedQueue: QueueType | null;
    public readonly setRequestedQueue: (queue: QueueType | null) => void;
    public readonly connected: boolean;
    public readonly error: ClientError | null;
    public readonly waitingPlayers: WaitingPlayerState[];
    public readonly activeLobbies: LobbySummary[];
    public readonly activeLobbyCounts: RulesetCounts;

    constructor(
        activeQueue: QueueState | null,
        requestedQueue: QueueType | null,
        setRequestedQueue: (queue: QueueType | null) => void,
        connected: boolean,
        error: ClientError | null,
        waitingPlayers: WaitingPlayerState[],
        activeLobbies: LobbySummary[],
    ) {
        this.activeQueue = activeQueue;
        this.requestedQueue = requestedQueue;
        this.setRequestedQueue = setRequestedQueue;
        this.connected = connected;
        this.error = error;
        this.waitingPlayers = waitingPlayers;
        this.activeLobbies = activeLobbies;
        this.activeLobbyCounts = RulesetCounts.countPlayersBySettings(activeLobbies);
    }

    public getWaitingPlayerCounts(settings: Ruleset): WaitingPlayerCounts {
        let ranked = 0;
        let casual = 0;
        for (const state of this.waitingPlayers) {
            if (state.settings === settings) {
                if (state.mode === GameMode.RANKED) {
                    ranked = state.waitingCount;
                } else {
                    casual = state.waitingCount;
                }
            }
        }
        return { ranked, casual };
    }

    /**
     * A band-aid to help with stopping seeing yourself as a waiting player.
     */
    public minusOneWaiting(mode: GameMode, settings: Ruleset) {
        for (const state of this.waitingPlayers) {
            if (state.mode === mode && state.settings === settings) {
                state.waitingCount = Math.max(0, state.waitingCount - 1);
            }
        }
    }
}


function entryListClassName(small: boolean): string {
    return cn(
        "flex flex-col gap-3 overflow-y-auto",
        small ? "min-h-[10em]" : "min-h-[26em]",
    );
}


interface LobbyEntryProps {
    small?: boolean;
    children: ReactNode;
}


function LobbyEntry({ small, children }: LobbyEntryProps) {
    return (
        <Panel
            size="sm"
            className={cn(
            "flex flex-row items-stretch overflow-hidden p-0 text-left border border-transparent",
            "gap-3 md:gap-4",
            small ? "min-h-0" : "min-h-[5.5em]",
        )}>
            {children}
        </Panel>
    );
}


interface LobbyEntryButtonsProps {
    children: ReactNode;
}


function LobbyEntryButtons({ children }: LobbyEntryButtonsProps) {
    return (
        <div className="flex flex-row items-center gap-2 p-3 bg-shade">
            {children}
        </div>
    );
}


interface CancelButtonProps {
    className?: string;
    wide: boolean;
    onClick: () => void;
}


function CancelButton({ className, wide, onClick }: CancelButtonProps) {
    return (
        <Button
            tone="danger"
            size="sm"
            className={cn(
                "flex flex-row justify-center items-center h-[2.6em] min-h-[2.6em]",
                wide ? "w-full" : "w-[2.6em]",
                className ?? null,
            )}
            onClick={onClick}>

            <InlineIcon>
                <CloseIcon />
            </InlineIcon>
        </Button>
    );
}


const DETAILS_TEXT_CLASSES = "m-0 text-base text-left";


interface LiveLobbiesListEntryProps {
    summary: LobbySummary;
    getWatchLink: (lobbyID: string) => Path;
}


function LiveLobbiesEntry({ summary, getWatchLink }: LiveLobbiesListEntryProps) {
    return (
        <LobbyEntry>
            <div className="p-3 pr-0">
                <RulesetPopupButton ruleset={summary.settings} />
            </div>

            <div className="flex items-center grow py-1">
                <p className={DETAILS_TEXT_CLASSES}>
                    <LobbyPlayerDisplayName player={summary.player1} />
                    {" vs. "}
                    <LobbyPlayerDisplayName player={summary.player2} />

                    {summary.mode === GameMode.ONLINE && " (Casual)"}
                    {summary.mode === GameMode.RANKED && " (Ranked)"}
                </p>
            </div>

            <LobbyEntryButtons>
                <Button
                    tone="accent"
                    size="sm"
                    href={getWatchLink(summary.lobbyID).toString()}>

                    Watch
                </Button>
            </LobbyEntryButtons>
        </LobbyEntry>
    );
}


interface LiveLobbiesEntryListProps {
    summaries: LobbySummary[];
    getWatchLink: (lobbyID: string) => Path;
}


function LiveLobbiesEntryList({ summaries, getWatchLink }: LiveLobbiesEntryListProps) {
    const elems = [];
    for (const summary of summaries) {
        elems.push(
            <LiveLobbiesEntry
                key={summary.lobbyID}
                summary={summary}
                getWatchLink={getWatchLink} />,
        );
    }
    return (
        <>
            {elems}
        </>
    );
}


interface PendingLobbyListEntryProps {
    ruleset: Ruleset | null;
    waitingRankedCount: number;
    waitingCasualCount: number;
    playingCount: number;
    rating?: number;
    matchmakingState: MatchmakingState;
    onClick?: () => void;
}


function PendingLobbyListEntry({
    ruleset, waitingRankedCount, waitingCasualCount, playingCount, rating, matchmakingState, onClick,
}: PendingLobbyListEntryProps) {

    const user = useOptionalUser();

    const [clickedPlay, setClickedPlay] = useState(false);
    const [loginPrompt, setLoginPrompt] = useState<GameMode | null>(null);

    const activeQueue = matchmakingState.activeQueue;
    const requestedQueue = matchmakingState.requestedQueue;

    const requestedAnyQueue = !!requestedQueue;
    const requestedThisQueue = (
        (ruleset && requestedQueue?.ruleset === ruleset)
        || (!ruleset && requestedAnyQueue)
    );

    const anyQueueActive = !!activeQueue;
    const inThisQueue = (
        (ruleset && activeQueue?.queueType?.ruleset === ruleset)
        || (!ruleset && anyQueueActive)
    );
    const inRankedQueue = (inThisQueue && activeQueue?.queueType?.gameMode === GameMode.RANKED);

    const buttonState: "play" | "options" | "cancel" = (
        requestedThisQueue ? "cancel" : (clickedPlay ? "options" : "play")
    );

    let errorMessage: string | null = null;
    if (matchmakingState.error) {
        errorMessage = matchmakingState.error.message;
    }

    let waitingDetails = "";
    let nonWaitingDetails = "";
    let highlightNonWaitingDetails = false;
    if (requestedQueue && requestedThisQueue) {
        if (matchmakingState.connected) {
            if (activeQueue && areQueueTypesEqual(activeQueue.queueType, requestedQueue)) {
                nonWaitingDetails = `Searching for a ${inRankedQueue ? "ranked" : "casual"} game`;
                highlightNonWaitingDetails = true;
            } else {
                nonWaitingDetails = "Joining queue";
            }
        } else {
            nonWaitingDetails = "Connecting";
        }
    } else {
        if (waitingRankedCount > 0) {
            waitingDetails += `${waitingRankedCount}\xa0waiting\xa0(ranked)`;
        }
        if (waitingCasualCount > 0) {
            if (waitingDetails !== "") {
                waitingDetails += ", ";
            }
            waitingDetails += `${waitingCasualCount}\xa0waiting\xa0(casual)`;
        }
        if (ruleset === null || playingCount > 0) {
            if (waitingDetails !== "") {
                waitingDetails += ", ";
            }
            nonWaitingDetails += `${playingCount}\xa0playing`;
        }
        if (ruleset === null) {
            nonWaitingDetails += " online";
        }
    }

    const joinRankedQueue = useCallback(() => startTransition(() => {
        if (!user)
            throw new Error("An account is required to join the ranked queue");

        matchmakingState.setRequestedQueue({
            gameMode: GameMode.RANKED,
            ruleset: ruleset ?? Ruleset.FINKEL,
        });
        setClickedPlay(false);
    }), [matchmakingState, ruleset, setClickedPlay, user]);

    const joinCasualQueue = useCallback(() => startTransition(() => {
        matchmakingState.setRequestedQueue({
            gameMode: GameMode.ONLINE,
            ruleset: ruleset ?? Ruleset.FINKEL,
        });
        setClickedPlay(false);
    }), [matchmakingState, ruleset, setClickedPlay]);

    return (
        <LobbyEntry small={ruleset === null}>
            {ruleset && (
                <div className="p-3 pr-0">
                    <RulesetPopupButton ruleset={ruleset} rating={rating} />
                </div>
            )}

            <div className={cn(
                "flex items-center grow",
                ruleset === null ? "pl-3 py-3" : "py-1",
            )}>
                {!clickedPlay && (
                    <AnimatePresence>
                        {(waitingDetails || nonWaitingDetails) && (
                            <FadingDiv key="details" options={{ duration: 0.2 }}>
                                {errorMessage && (
                                    <p className={cn(DETAILS_TEXT_CLASSES, "text-danger")}>
                                        {errorMessage}
                                    </p>
                                )}

                                {!errorMessage && (
                                    <p
                                        className={cn(
                                            DETAILS_TEXT_CLASSES,
                                            onClick ? "cursor-pointer hover:underline" : null,
                                        )}
                                        onClick={onClick}>
                                        {waitingDetails && (
                                            <span className="text-accent">
                                                {waitingDetails}
                                            </span>
                                        )}
                                        {nonWaitingDetails && (
                                            <span className={cn(
                                                highlightNonWaitingDetails ? "text-primary" : null,
                                            )}>
                                                {nonWaitingDetails}

                                                {requestedThisQueue && (
                                                    <DotDotDot enabled={true} />
                                                )}
                                            </span>
                                        )}
                                    </p>
                                )}
                            </FadingDiv>
                        )}
                    </AnimatePresence>
                )}
            </div>

            <LobbyEntryButtons>

                {buttonState === "play" && (
                    <Button
                        size="sm"
                        tone={ruleset ? "accent" : "primary"}
                        className={cn("min-h-[2.6em]", ruleset ? "w-full" : null)}
                        onClick={() => {
                            if (ruleset !== null) {
                                setClickedPlay(true);
                                return;
                            }

                            if (user) {
                                joinRankedQueue();
                            } else {
                                setLoginPrompt(GameMode.RANKED);
                            }
                        }}>

                        {ruleset ? "Play" : "Play Online"}
                    </Button>
                )}

                {buttonState === "options" && (
                    <>
                        <Button
                            tone="accent"
                            size="sm"
                            className="min-h-[2.6em] px-2"
                            title="Play a competitive rated game"
                            onClick={() => {
                                if (user) {
                                    joinRankedQueue();
                                } else {
                                    setLoginPrompt(GameMode.RANKED);
                                }
                            }}>

                            Ranked
                        </Button>

                        <Button
                            tone="primary"
                            size="sm"
                            className="min-h-[2.6em] px-2"
                            title="Play a casual unrated game"
                            onClick={() => {
                                if (user) {
                                    joinCasualQueue();
                                } else {
                                    setLoginPrompt(GameMode.ONLINE);
                                }
                            }}>

                            Casual
                        </Button>

                        <CancelButton
                            className="px-2"
                            wide={false}
                            onClick={() => {
                                setClickedPlay(false);
                            }} />
                    </>
                )}

                {buttonState === "cancel" && (
                    <CancelButton
                        wide={true}
                        onClick={() => startTransition(() => {
                            setClickedPlay(false);
                            matchmakingState.setRequestedQueue(null);
                        })} />
                )}
            </LobbyEntryButtons>

            {!user && (
                <>
                    <LoginPrompt
                        visible={!!loginPrompt}
                        close={() => setLoginPrompt(null)}
                        onNoThanks={() => startTransition(() => {
                            setLoginPrompt(null);
                            if (loginPrompt === GameMode.ONLINE) {
                                joinCasualQueue();
                            }
                        })}
                        title={
                            loginPrompt === GameMode.RANKED
                                ? "You need an account to play ranked"
                                : undefined
                        }
                        noThanksText={
                            loginPrompt === GameMode.RANKED
                                ? "Cancel"
                                : "Play as guest"
                        }>

                        <p>
                            {loginPrompt === GameMode.ONLINE && (
                                <>
                                    Online games are better when you&apos;re not anonymous!
                                </>
                            )}

                            {loginPrompt === GameMode.RANKED && (
                                <>
                                    You can still play casual games, or games against the computer,
                                    without an account.
                                </>
                            )}
                        </p>
                    </LoginPrompt>
                </>
            )}
        </LobbyEntry>
    );
}


interface PendingLobbiesListProps {
    matchmakingState: MatchmakingState;
    ratings?: Record<string, number> | null;
    small?: boolean;
    onWatchClick?: () => void;
}


function PendingLobbiesList({
    matchmakingState, ratings, small, onWatchClick,
}: PendingLobbiesListProps) {
    const finkelWaiting = matchmakingState.getWaitingPlayerCounts(Ruleset.FINKEL);
    const blitzWaiting = matchmakingState.getWaitingPlayerCounts(Ruleset.BLITZ);
    const mastersWaiting = matchmakingState.getWaitingPlayerCounts(Ruleset.MASTERS);
    const rankedWaiting = (finkelWaiting.ranked + blitzWaiting.ranked + mastersWaiting.ranked);
    const casualWaiting = (finkelWaiting.casual + blitzWaiting.casual + mastersWaiting.casual);

    const getRating = (ruleset: Ruleset): number | undefined => {
        if (!ratings)
            return undefined;
        const rating = ratings[ruleset.getID()];
        return rating !== undefined ? rating : 600;
    };

    return (
        <>
            {!small && (
                <>
                    <PendingLobbyListEntry
                        ruleset={Ruleset.FINKEL}
                        waitingRankedCount={finkelWaiting.ranked}
                        waitingCasualCount={finkelWaiting.casual}
                        playingCount={matchmakingState.activeLobbyCounts.finkelCount}
                        rating={getRating(Ruleset.FINKEL)}
                        matchmakingState={matchmakingState}
                        onClick={matchmakingState.activeLobbyCounts.finkelCount > 0 ? onWatchClick : undefined} />

                    <PendingLobbyListEntry
                        ruleset={Ruleset.BLITZ}
                        waitingRankedCount={blitzWaiting.ranked}
                        waitingCasualCount={blitzWaiting.casual}
                        playingCount={matchmakingState.activeLobbyCounts.blitzCount}
                        rating={getRating(Ruleset.BLITZ)}
                        matchmakingState={matchmakingState}
                        onClick={matchmakingState.activeLobbyCounts.blitzCount > 0 ? onWatchClick : undefined} />

                    <PendingLobbyListEntry
                        ruleset={Ruleset.MASTERS}
                        waitingRankedCount={mastersWaiting.ranked}
                        waitingCasualCount={mastersWaiting.casual}
                        playingCount={matchmakingState.activeLobbyCounts.mastersCount}
                        rating={getRating(Ruleset.MASTERS)}
                        matchmakingState={matchmakingState}
                        onClick={matchmakingState.activeLobbyCounts.mastersCount > 0 ? onWatchClick : undefined} />
                </>
            )}

            {small && (
                <PendingLobbyListEntry
                    ruleset={null}
                    waitingRankedCount={rankedWaiting}
                    waitingCasualCount={casualWaiting}
                    playingCount={matchmakingState.activeLobbyCounts.totalCount}
                    matchmakingState={matchmakingState}
                    onClick={matchmakingState.activeLobbyCounts.totalCount > 0 ? onWatchClick : undefined} />
            )}

            <Panel
                size="sm"
                className={cn("text-left border border-transparent", small ? "p-2" : "p-3")}>
                <p>
                    <Link href={new Path(
                        "/game",
                        (
                            small
                                ? { mode: "friend", rules: "finkel", tab: "play" }
                                : { mode: "friend", tab: "rules" }
                        ),
                    ).toString()}>
                        Or, create a link to send to a friend!
                    </Link>
                </p>
            </Panel>
        </>
    );
}


interface MatchmakingControlsProps {
    className?: string;
    clientControls: GameClientControls;
    getWatchLink: (lobbyID: string) => Path;
    ratings?: Record<string, number> | null;
    small?: boolean;
    lobbyManagerState: LobbyManagerStateResult;
}


export function MatchmakingControls({
    className, clientControls, getWatchLink, ratings, small, lobbyManagerState,
}: MatchmakingControlsProps) {
    const [tab, setTab] = useState<"play" | "watch">("play");

    const gameAPI = useGameAPI();

    const waitingPlayers: WaitingPlayerState[] = useMemo(() => {
        return lobbyManagerState.data?.waitingPlayers ?? [];
    }, [lobbyManagerState.data?.waitingPlayers]);

    const activeLobbies: LobbySummary[] = useMemo(() => {
        return lobbyManagerState.data?.startedLobbies ?? [];
    }, [lobbyManagerState.data?.startedLobbies]);

    const joinLobby = useCallback((lobbyID: string) => {
        const settings = new RemoteLobbySettings(lobbyID);
        clientControls.startGame(settings);
    }, [clientControls]);

    const {
        activeQueue,
        requestedQueue,
        setRequestedQueue,
        connected,
        error,
    } = useMatchmaking(joinLobby, gameAPI);

    const matchmakingState = useMemo(() => new MatchmakingState(
        activeQueue, requestedQueue, setRequestedQueue, connected, error, waitingPlayers, activeLobbies,
    ), [activeQueue, requestedQueue, setRequestedQueue, connected, error, waitingPlayers, activeLobbies]);

    // Hack to stop players seeing themselves as waiting.
    useEffect(() => {
        const queueType = activeQueue?.queueType;
        if (queueType) {
            matchmakingState.minusOneWaiting(queueType.gameMode, queueType.ruleset);
        }
    }, [matchmakingState, activeQueue]);

    // Automatically switch back to play tab if there are no active lobbies
    useEffect(() => {
        if (tab === "watch" && activeLobbies.length === 0) {
            setTab("play");
        }
    }, [tab, activeLobbies.length]);

    let statusElem = null;
    if (!lobbyManagerState.data || lobbyManagerState.loading || lobbyManagerState.error) {
        let status = "Loading live games...";
        if (lobbyManagerState.error) {
            status = `Error loading live games: ${lobbyManagerState.error.message}`;
            console.error(status);
        }
        if (!small) {
            statusElem = (
                <p>{status}</p>
            );
        } else {
            statusElem = (<></>);
        }
    }

    return (
        <div className={className}>
            <motion.div
                className="flex flex-col gap-4"
                {...fadeInOut()}>

                <div>
                    <AnimatePresence mode="wait">
                        {statusElem && (
                            <motion.div
                                className={entryListClassName(!!small)}
                                {...fadeInOut({ delay: lobbyManagerState.loading ? 0.25 : 0.0 })}>

                                {statusElem}
                            </motion.div>
                        )}

                        {!statusElem && tab === "play" && (
                            <motion.div
                                key="play"
                                className={entryListClassName(!!small)}
                                {...fadeInOut()}>

                                <PendingLobbiesList
                                    matchmakingState={matchmakingState}
                                    ratings={ratings}
                                    small={small}
                                    onWatchClick={() => setTab("watch")} />
                            </motion.div>
                        )}

                        {!statusElem && tab === "watch" && (
                            <motion.div
                                key="watch"
                                className={entryListClassName(false)}
                                {...fadeInOut()}>

                                <div className="flex justify-center p-3 pb-4">
                                    <Button
                                        tone="primary"
                                        onClick={() => setTab("play")}>
                                        Back to Play
                                    </Button>
                                </div>

                                {activeLobbies.length > 0 && (
                                    <LiveLobbiesEntryList
                                        summaries={activeLobbies}
                                        getWatchLink={getWatchLink} />
                                )}

                                {activeLobbies.length === 0 && (
                                    <p className="p-4">
                                        The boards are quiet right now
                                    </p>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
}
